/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import React from 'react';
import { Paper, Separator } from '@vaisala-rockhopper/components';
import CalculatorCardHeading from './CalculatorCardHeading';
import CalculatorCardRow from './CalculatorCardRow';
import CalculatorCardSubHeading from './CalculatorCardSubHeading';
import './CalculatorCard.scss';
import { CalculatorResult, ParameterModifier, ParameterName } from '../../../modules/calculator/calculatorTypes';
import { ModalType } from '../modals/Modals';
import { OptionsConfig } from '../modals/OptionsModal';
import { isMobileOnly } from 'react-device-detect';
import { UnitPreset } from '../../../modules/units/unitTypes';

interface CalculatorCardProps {
  config: any;
  cardIndex: number;
  phase: number;
  active: boolean;
  currentResult?: CalculatorResult;
  onParameterChange: (parameterName: ParameterName, value: ParameterModifier, phase?: number) => void;
  variant?: string;
  openModal: (type: ModalType, title?: string, optionsConfig?: OptionsConfig, onAccept?: VoidFunction, onReject?: VoidFunction) => void;
  cardsLength: number;
  currentUnitPreset: UnitPreset | undefined;
  theme: string;
  activeMethod: number;
}

const CalculatorCard = React.forwardRef(
  (
    {
      config,
      cardIndex,
      phase,
      active,
      onParameterChange,
      currentResult,
      variant,
      openModal,
      cardsLength,
      currentUnitPreset,
      theme,
      activeMethod,
    }: CalculatorCardProps,
    ref: any
  ) => {
    const inputs = config.inputs;
    const outputs = config.outputs;
    const manualUnit = currentUnitPreset?.mode === 'manual';

    const inputRows = inputs
      .filter((paramConfig: any, i: number) => {
        if (variant === 'graph') {
          return paramConfig.graph;
        } else return paramConfig;
      })
      .sort((a: any, b: any) => {
        return a.graphOrder - b.graphOrder;
      })
      .map((paramConfig: any, i: number) => (
        <React.Fragment key={i}>
          {paramConfig.type === 'subheading' && (
            <CalculatorCardSubHeading config={paramConfig} cardIndex={cardIndex} currentResult={currentResult} variant={variant} />
          )}
          {paramConfig.type !== 'subheading' && (
            <CalculatorCardRow
              rowType={variant !== 'graph' ? paramConfig.type : 'plain'}
              config={paramConfig}
              cardIndex={cardIndex}
              manualUnit={manualUnit}
              onParameterChange={onParameterChange}
              currentResult={currentResult}
              variant={variant}
              theme={theme}
              activeMethod={activeMethod}
            />
          )}
        </React.Fragment>
      ));

    const outputRows = outputs
      .filter((paramConfig: any, i: number) => {
        if (variant === 'graph') {
          return paramConfig.graph;
        } else return paramConfig;
      })
      .sort((a: any, b: any) => {
        return a.graphOrder - b.graphOrder;
      })
      .map((paramConfig: any, i: number) => (
        <CalculatorCardRow
          key={i}
          rowType={'plain'}
          config={paramConfig}
          cardIndex={cardIndex}
          manualUnit={manualUnit}
          onParameterChange={onParameterChange}
          currentResult={currentResult}
          variant={variant}
          theme={theme}
          activeMethod={activeMethod}
        />
      ));

    return (
      <div ref={ref}>
        <Paper
          id={`${variant === 'graph' ? 'graph-' : ''}phase-${phase}`}
          className={`calculator-card ${variant ? `calculator-card--${variant}` : 'calculator-card--default'} ${
            isMobileOnly ? 'calculator-card--mobile' : 'calculator-card--desktop'
          }`}
        >
          <CalculatorCardHeading title={config.titleKey} phase={phase} active={active} openModal={openModal} cardsLength={cardsLength} />
          {variant !== 'graph' && config.dehumidification && (
            <CalculatorCardRow
              rowType="toggle"
              config={{ labelKey: 'ui.dehumidification' }}
              cardIndex={cardIndex}
              manualUnit={manualUnit}
              onParameterChange={onParameterChange}
              currentResult={currentResult}
              variant={variant}
              theme={theme}
              activeMethod={activeMethod}
            />
          )}
          {variant !== 'graph' ? (
            <>
              {inputRows}
              {outputs && outputs.length > 0 && <Separator />}
              {outputRows}
            </>
          ) : (
            <>
              {outputRows}
              {inputRows}
            </>
          )}
        </Paper>
      </div>
    );
  }
);

export default CalculatorCard;
